import React, { useCallback, useMemo, useState } from 'react';
import type { TargetType } from '@atlassiansox/cross-flow-base-types';
import { Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { useIntl } from '@atlassian/jira-intl';
import { TEMPLATE_KEY as PRIORITIZATION_KEY } from '@atlassian/jira-project-template-select-v2/src/utils/templates/product-discovery/prioritization/index.tsx';
import {
	ContextualAnalyticsData,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useFlagsService } from '@atlassian/jira-flags';
import type { CreateProjectResponse } from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useCanProductUserUpgradeAndPay } from '@atlassian/jira-billing/src/controllers/billing-permissions/index.tsx';
import onboardingCommonMessages from '../../common/messages.tsx';
import { OnboardingModalComponent } from '../../common/ui/modal/index.tsx';
import { OnboardingGrid } from '../../common/ui/onboarding-grid/index.tsx';
import { OnboardingContent } from '../../common/ui/content/index.tsx';
import { LegalNotice } from '../../common/ui/content/legal-notice/index.tsx';
import backgroundLight from '../../common/assets/background-light.png';
import backgroundDark from '../../common/assets/background-dark.png';
import { Features } from '../../common/ui/features/index.tsx';
import { TabContent } from '../../common/ui/tab-content/index.tsx';
import { getTemplateContentFromKey } from '../../utils/get-template-content-from-key/index.tsx';
import { useProjectCreationHandler } from '../../utils/use-project-creation-handler/index.tsx';
import type { TemplateKey } from '../../utils/types.tsx';

import { OnboardingModalContextProvider } from '../../controllers/onboarding-modal-context/index.tsx';
import messages from './messages.tsx';

export type ProjectDirectoryOnboardingModalProps = {
	targetProduct: Extract<TargetType, 'jira-product-discovery'>;
	sourceComponent: string;
	templateKey?: TemplateKey;
};

export const ProjectDirectoryOnboardingModal = ({
	targetProduct,
	templateKey = PRIORITIZATION_KEY,
	sourceComponent,
}: ProjectDirectoryOnboardingModalProps): React.JSX.Element => {
	const { push } = useRouterActions();
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const [selectedFeature, setSelectedFeature] = useState(0);
	const [open, setOpen] = useState(true);
	const [hasConsented, setHasConsented] = useState(false);
	const { hasPaymentDetailsOnFile, isLoading: isLoadingBillingPermissions } =
		useCanProductUserUpgradeAndPay({
			skip: false,
			productKey: 'jira-software.ondemand', // use source product as product key to find if has payment details
		});

	const handleLegalNoticeChange = useCallback(
		(value: boolean) => {
			setHasConsented(value);
		},
		[setHasConsented],
	);

	const handleFeatureUpdate = useCallback(
		(index: number) => setSelectedFeature(index),
		[setSelectedFeature],
	);

	const templateContent = useMemo(() => {
		return getTemplateContentFromKey(templateKey);
	}, [templateKey]);

	const onSuccess = useCallback(
		(
			createProjectResponse: CreateProjectResponse,
			_e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
			_analyticsEvent: UIAnalyticsEvent,
		) => {
			push(createProjectResponse.returnUrl);
		},
		[push],
	);

	const onFail = useCallback(
		(_error: Error) => {
			showFlag({
				type: 'error',
				id: 'project-creation-failure',
				title: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? onboardingCommonMessages.createProjectFailureTitleNonFinalGalaxia
						: onboardingCommonMessages.createProjectFailureTitleNonFinal,
				),
				description: formatMessage(
					onboardingCommonMessages.createProjectFailureDescriptionNonFinal,
				),
				messageId:
					'cross-flow-onboarding-modal.ui.project-directory-onboarding-modal.show-flag.error',
				messageType: 'transactional',
			});
		},
		[formatMessage, showFlag],
	);

	const {
		handleProjectCreation,
		isCreating,
		isUpgrading,
		isDisabled,
		targetEdition,
		shouldUpgrade,
	} = useProjectCreationHandler({
		templateKey,
		templateContent,
		targetProduct,
		hasPaymentDetails: hasPaymentDetailsOnFile || false,
		onSuccess,
		onFail,
		sourceComponent: 'projectDirectoryOnboardingModal',
	});

	return (
		<ContextualAnalyticsData
			attributes={{
				featureItemIndex: selectedFeature,
				templateKey,
				message: templateContent.features[selectedFeature].description.defaultMessage,
			}}
		>
			<OnboardingModalContextProvider targetProduct={targetProduct} open={open}>
				<OnboardingModalComponent
					targetProduct={targetProduct}
					sourceComponent={sourceComponent}
					ariaLabel={formatMessage(messages.ariaLabel)}
					title={formatMessage(messages.modalHeading)}
					onClose={() => setOpen(false)}
				>
					<OnboardingGrid enableCrossButton={false} onClose={() => setOpen(false)}>
						<OnboardingGrid.ContentSection>
							<OnboardingContent>
								<Heading size="large">{formatMessage(messages.modalHeading)}</Heading>
								<Features>
									{templateContent.features.map((item, i) => (
										<Features.Item
											isActive={selectedFeature === i}
											key={`cross-flow-onboarding-modal-${i}-item`}
											onClick={() => handleFeatureUpdate(i)}
											index={i}
										>
											<item.icon label="" color="var(--ds-icon-selected)" key={i} />
											<Text color="inherit">{formatMessage(item.description)}</Text>
										</Features.Item>
									))}
								</Features>
								{isLoadingBillingPermissions || isDisabled ? (
									<>
										<Skeleton width="100%" height={token('space.075')} />
										<Skeleton width="90%" height={token('space.075')} />
										<Skeleton width="50%" height={token('space.075')} />
									</>
								) : (
									<LegalNotice
										hasPaymentDetails={hasPaymentDetailsOnFile}
										onChange={handleLegalNoticeChange}
										edition={targetEdition || 'STANDARD_EDITION'}
										shouldUpgrade={shouldUpgrade}
									/>
								)}
							</OnboardingContent>

							<OnboardingGrid.Controls>
								<OnboardingGrid.ControlItem variant="cancel" onClick={() => setOpen(false)} />
								<OnboardingGrid.ControlItem
									variant="get-started"
									appearance="primary"
									isLoading={isCreating || isUpgrading}
									onClick={handleProjectCreation}
									disabled={
										isDisabled ||
										(targetEdition !== 'FREE_EDITION' &&
											targetEdition !== 'UNLICENSED' &&
											!!hasPaymentDetailsOnFile &&
											!hasConsented &&
											shouldUpgrade)
									}
								/>
							</OnboardingGrid.Controls>
						</OnboardingGrid.ContentSection>
						<OnboardingGrid.ImageSection
							backgroundLight={backgroundLight}
							backgroundDark={backgroundDark}
						>
							<TabContent
								onChange={handleFeatureUpdate}
								selected={selectedFeature}
								content={templateContent}
								templateKey={templateKey}
							/>
						</OnboardingGrid.ImageSection>
					</OnboardingGrid>
				</OnboardingModalComponent>
			</OnboardingModalContextProvider>
		</ContextualAnalyticsData>
	);
};
